import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import PartnerAgreement from "../components/PartnerAgreement";
import usePageTitle from "../hooks/usePageTitle";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Store, CheckCircle, Loader2, Upload, Image, X, Globe, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BusinessSignupPage = () => {
  usePageTitle('Become a Partner', 'Join Advanta Plus as a partner business and attract new customers');
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [showAgreement, setShowAgreement] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [subLocations, setSubLocations] = useState([]);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    description: "",
    address: "",
    category: "dining",
    discount_level: "15",
    website: "",
    tour_3d_url: "",
    sub_location: ""
  });

  useEffect(() => {
    const fetchSubLocations = async () => {
      try {
        const response = await axios.get(`${API}/sub-locations`);
        setSubLocations(response.data);
      } catch (error) {
        console.error("Failed to fetch sub-locations:", error);
      }
    };
    fetchSubLocations();
  }, []);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (!formData.sub_location) {
      toast.error("Please select a location area");
      return;
    }

    // Submit the application straight through — the signed agreement is now
    // optional and can be added by an admin from the dashboard later.
    submitApplication(null, null);
  };

  const submitApplication = async (signedPdfBlob, discountInfo) => {
    setShowAgreement(false);
    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append("name", formData.name);
      submitData.append("email", formData.email);
      submitData.append("phone", formData.phone || "");
      submitData.append("business_name", formData.business_name);
      submitData.append("description", formData.description);
      submitData.append("address", formData.address);
      submitData.append("category", formData.category);
      submitData.append("discount_level", formData.discount_level);
      submitData.append("website", formData.website || "");
      submitData.append("tour_3d_url", formData.tour_3d_url || "");
      submitData.append("sub_location", formData.sub_location);
      if (imageFile) submitData.append("image", imageFile);
      if (signedPdfBlob) {
        const safeName = (formData.business_name || "application").replace(/\s+/g, "_");
        submitData.append("signed_agreement", signedPdfBlob, `agreement_${safeName}.pdf`);
      }
      if (discountInfo?.discount_percent != null) submitData.append("discount_percent", discountInfo.discount_percent);
      if (discountInfo?.discount_applies_to) submitData.append("discount_applies_to", discountInfo.discount_applies_to);

      await axios.post(`${API}/business-applications`, submitData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  const buildDraftFormData = (signedPdfBlob = null, discountInfo = null) => {
    const fd = new FormData();
    Object.entries(formData).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") fd.append(k, v);
    });
    if (imageFile) fd.append("image", imageFile);
    if (signedPdfBlob) {
      const safeName = (formData.business_name || "draft").replace(/\s+/g, "_");
      fd.append("signed_agreement", signedPdfBlob, `agreement_${safeName}.pdf`);
    }
    if (discountInfo) {
      if (discountInfo.discount_percent != null) fd.append("discount_percent", discountInfo.discount_percent);
      if (discountInfo.discount_applies_to) fd.append("discount_applies_to", discountInfo.discount_applies_to);
    }
    if (draftId) fd.append("draft_id", draftId);
    return fd;
  };

  const handleSaveDraft = async () => {
    if (!formData.email && !formData.business_name) {
      toast.error("Enter at least an email or business name before saving a draft");
      return;
    }
    setSavingDraft(true);
    try {
      const res = await axios.post(`${API}/business-applications/draft`, buildDraftFormData(), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.id) setDraftId(res.data.id);
      setDraftSaved(true);
      toast.success("Draft saved — you can finish it later from the admin panel");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSignAndSaveDraft = async (signedPdfBlob, discountInfo) => {
    setShowAgreement(false);
    setSavingDraft(true);
    try {
      const res = await axios.post(`${API}/business-applications/draft`, buildDraftFormData(signedPdfBlob, discountInfo), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.id) setDraftId(res.data.id);
      setDraftSaved(true);
      toast.success("Draft saved with signed agreement — finish the details from the admin panel when ready");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  // Kept for the optional "sign now" path — reuses the same submission flow.
  const handleSignAgreement = (signedPdfBlob, discountInfo) => submitApplication(signedPdfBlob, discountInfo);

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-[#047857]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#047857]" />
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl text-white mb-4">
            Application Received!
          </h1>
          <p className="text-white/50 mb-8">
            Thank you for applying to become an Advanta Plus partner. We'll review your application and get back to you within 2-3 business days.
          </p>
          <Link to="/">
            <Button className="btn-gold px-8 py-4 rounded">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] py-12 px-6">
      {/* Back Button */}
      <Link 
        to="/"
        className="fixed top-6 left-6 flex items-center gap-2 text-white/60 hover:text-[#D4AF37] transition-colors z-10"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>Back</span>
      </Link>

      <div className="max-w-2xl mx-auto pt-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
            <Store className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl text-white mb-2">
            Become a Partner Business
          </h1>
          <p className="text-white/50">
            Join Advanta Plus's network and attract new customers with exclusive member discounts
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-3 gap-4 mb-10">
          {[
            { title: "Free Listing", desc: "No signup fees" },
            { title: "New Customers", desc: "Access to members" },
            { title: "Easy Validation", desc: "Simple QR scanner" },
          ].map((benefit, i) => (
            <div key={i} className="bg-[#0B0F19] rounded-xl p-4 text-center border border-[#D4AF37]/10">
              <p className="text-[#D4AF37] font-medium">{benefit.title}</p>
              <p className="text-white/40 text-xs mt-1">{benefit.desc}</p>
            </div>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleFormSubmit} className="bg-[#0B0F19] rounded-2xl p-8 border border-[#D4AF37]/10">
          <h2 className="font-['Playfair_Display'] text-xl text-white mb-6">Contact Information</h2>
          
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="space-y-2">
              <Label className="text-white/70">Your Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="John Smith"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                placeholder="john@business.com"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
          </div>

          <div className="space-y-2 mb-8">
            <Label className="text-white/70">Phone Number</Label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              placeholder="+34 612 345 678"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
          </div>

          <h2 className="font-['Playfair_Display'] text-xl text-white mb-6">Business Details</h2>

          <div className="space-y-2 mb-4">
            <Label className="text-white/70">Business Name *</Label>
            <Input
              value={formData.business_name}
              onChange={(e) => setFormData({...formData, business_name: e.target.value})}
              required
              placeholder="Restaurant El Sol"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
          </div>

          <div className="space-y-2 mb-4">
            <Label className="text-white/70">Description *</Label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              required
              placeholder="Tell us about your business..."
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          <div className="space-y-2 mb-4">
            <Label className="text-white/70">Address *</Label>
            <Input
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              required
              placeholder="Calle Principal 123, Marbella"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
          </div>

          <div className="space-y-2 mb-4">
            <Label className="text-white/70 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Location Area *
            </Label>
            <Select value={formData.sub_location} onValueChange={(v) => setFormData({...formData, sub_location: v})}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Select location area" />
              </SelectTrigger>
              <SelectContent>
                {subLocations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="space-y-2">
              <Label className="text-white/70">Category *</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dining">Dining & Restaurants</SelectItem>
                  <SelectItem value="shopping">Shopping & Retail</SelectItem>
                  <SelectItem value="services">Services & Experiences</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Discount You'll Offer *</Label>
              <Select value={formData.discount_level} onValueChange={(v) => setFormData({...formData, discount_level: v})}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10% off</SelectItem>
                  <SelectItem value="15">15% off</SelectItem>
                  <SelectItem value="20">20% off</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Image Upload */}
          <div className="space-y-2 mb-4">
            <Label className="text-white/70">Business Photo</Label>
            <div className="mt-2">
              {imagePreview ? (
                <div className="relative w-full h-48 rounded-xl overflow-hidden">
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 p-2 rounded-full"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-[#D4AF37]/50 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-8 h-8 text-white/40 mb-2" />
                    <p className="text-sm text-white/50">Click to upload image</p>
                    <p className="text-xs text-white/30 mt-1">PNG, JPG, WebP (max 5MB)</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleImageSelect}
                  />
                </label>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div className="space-y-2">
              <Label className="text-white/70">Website (optional)</Label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData({...formData, website: e.target.value})}
                placeholder="https://www.yourbusiness.com"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                3D Virtual Tour URL
              </Label>
              <Input
                value={formData.tour_3d_url}
                onChange={(e) => setFormData({...formData, tour_3d_url: e.target.value})}
                placeholder="https://my.matterport.com/..."
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="btn-gold w-full py-6 rounded text-base"
            disabled={loading || savingDraft}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Application"
            )}
          </Button>

          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              if (!formData.sub_location) {
                toast.error("Please select a location area");
                return;
              }
              setShowAgreement(true);
            }}
            disabled={loading || savingDraft}
            className="w-full py-5 mt-3 rounded text-sm text-white/70 hover:text-white border border-white/10 hover:border-[#D4AF37]/40"
          >
            Sign the partner agreement now (optional)
          </Button>

          <Button
            type="button"
            variant="ghost"
            onClick={handleSaveDraft}
            disabled={loading || savingDraft}
            className="w-full py-3 mt-2 rounded text-xs text-white/50 hover:text-white/80"
          >
            {savingDraft ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving draft...
              </>
            ) : (
              draftId ? "Update Saved Draft" : "Save Draft"
            )}
          </Button>

          <p className="text-white/30 text-xs text-center mt-4">
            You can submit your application now and sign the partner agreement later — our team will be in touch to finalise it. If you'd rather sign now, use the optional button above.
          </p>
        </form>
      </div>

      {/* Partner Agreement Modal */}
      {showAgreement && (
        <PartnerAgreement
          businessName={formData.business_name}
          contactName={formData.name}
          onSign={handleSignAgreement}
          onSaveDraft={handleSignAndSaveDraft}
          onCancel={() => setShowAgreement(false)}
        />
      )}
    </div>
  );
};

export default BusinessSignupPage;
